import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { styledToast as toast } from "@/components/ui/sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "./StatusBadge";
import NotificationBell from "./NotificationBell";
import LanguageSwitcher from "./LanguageSwitcher";
import AnalyticsDashboard from "./AnalyticsDashboard";
import OfficialsManagement from "./OfficialsManagement";
import LocationBasedReports from "./LocationBasedReports";
import ExportButtons from "./ExportButtons";
import AuditLogs from "./AuditLogs";
import GOKOfficialsManagement from "./GOKOfficialsManagement";
import { motion } from "framer-motion";
import { 
  LogOut, Search, FileText, CheckCircle, Clock, XCircle, 
  TrendingUp, Users, MapPin, Shield, Crown, Building2, 
  Globe, Filter 
} from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from 'react-i18next';
import { LoadingOverlay } from "./LoadingOverlay";

export default function GOKDashboard({ user }) {
  const { t } = useTranslation();
  const [applications, setApplications] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, [departmentFilter]);

  useEffect(() => {
    filterApplications();
  }, [applications, searchQuery, statusFilter]);

  const loadData = async () => {
    try {
      const deptParam = departmentFilter !== "all" ? `?department=${departmentFilter}` : "";
      
      const [appsRes, statsRes] = await Promise.all([
        api.get(`/applications${deptParam}`),
        api.get(`/dashboard/stats${deptParam}`),
      ]);
      setApplications(appsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(t('common.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = applications;

    if (searchQuery) {
      filtered = filtered.filter(
        (app) =>
          app.farmer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          app.land_survey_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
          app.village.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((app) => app.status === statusFilter);
    }

    setFilteredApps(filtered);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const getDesignationLabel = (designation) => {
    const labels = {
      'ac': t('gok.ac'),
      'dc': t('gok.dc'),
      'gok_secretary': t('gok.secretary'),
      'principal_secretary': t('gok.principalSecretary'),
      'minister': t('gok.minister'),
    };
    return labels[designation] || designation;
  };

  if (loading) {
    return <LoadingOverlay />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-100 via-amber-50/30 to-stone-100">
      {/* GOK Premium Header */}
      <header className="bg-gradient-to-r from-amber-800 via-amber-700 to-amber-800 border-b border-amber-600 sticky top-0 z-50 shadow-lg">
        <div className="w-full px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex justify-between items-center">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Crown className="w-6 h-6 text-amber-200" />
                <h1 className="text-lg sm:text-2xl font-manrope font-bold text-white tracking-tight truncate">
                  {t('appName')}
                </h1>
                <Badge className="bg-amber-500/30 text-amber-100 border-amber-400/50 ml-2 hidden sm:inline-flex">
                  <Globe className="w-3 h-3 mr-1" />
                  GOK
                </Badge>
              </div>
              <p className="text-xs sm:text-sm text-amber-200 truncate">
                {t('gok.welcomeGOK')}, {user?.name} 
                <span className="font-bold text-white"> ({t(`designation.${user?.designation}`)})</span>
              </p>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 ml-2">
              <div className="hidden sm:block">
                <ExportButtons userRole={user?.role} userDesignation={user?.designation} />
              </div>
              <LanguageSwitcher />
              <NotificationBell />
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="hover:bg-amber-600/50 text-amber-100 px-2 sm:px-4"
                data-testid="gok-logout-button"
              >
                <LogOut className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('dashboard.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 py-4 sm:py-8">
        <Tabs defaultValue="applications" className="w-full" data-testid="gok-dashboard-tabs">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 mb-4 sm:mb-6">
            <TabsList className="bg-amber-100 p-1 inline-flex min-w-max sm:flex sm:flex-wrap gap-1">
              <TabsTrigger 
                value="applications" 
                data-testid="gok-applications-tab"
                className="data-[state=active]:bg-amber-700 data-[state=active]:text-white hover:bg-amber-200 text-amber-900 text-xs sm:text-sm whitespace-nowrap"
              >
                <Globe className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                {t('gok.allApplications')}
              </TabsTrigger>
              <TabsTrigger 
                value="analytics" 
                data-testid="gok-analytics-tab"
                className="data-[state=active]:bg-amber-700 data-[state=active]:text-white hover:bg-amber-200 text-amber-900 text-xs sm:text-sm whitespace-nowrap"
              >
                {t('analytics.analyticsTab')}
              </TabsTrigger>
              <TabsTrigger 
                value="officials" 
                data-testid="gok-officials-tab"
                className="data-[state=active]:bg-amber-700 data-[state=active]:text-white hover:bg-amber-200 text-amber-900 text-xs sm:text-sm whitespace-nowrap"
              >
                <Users className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{t('officialsManagement.title')}</span>
                <span className="sm:hidden">{t('tabs.officialsShort')}</span>
              </TabsTrigger>
              <TabsTrigger 
                value="gok-management" 
                data-testid="gok-management-tab"
                className="data-[state=active]:bg-amber-700 data-[state=active]:text-white hover:bg-amber-200 text-amber-900 text-xs sm:text-sm whitespace-nowrap"
              >
                <Crown className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{t('gok.gokManagement')}</span>
                <span className="sm:hidden">GOK</span>
              </TabsTrigger>
              <TabsTrigger 
                value="location-reports" 
                data-testid="gok-location-reports-tab"
                className="data-[state=active]:bg-amber-700 data-[state=active]:text-white hover:bg-amber-200 text-amber-900 text-xs sm:text-sm whitespace-nowrap"
              >
                <MapPin className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{t('locationReports.tabTitle')}</span>
                <span className="sm:hidden">{t('tabs.locationShort')}</span>
              </TabsTrigger>
              <TabsTrigger 
                value="audit-logs" 
                data-testid="gok-audit-logs-tab"
                className="data-[state=active]:bg-amber-700 data-[state=active]:text-white hover:bg-amber-200 text-amber-900 text-xs sm:text-sm whitespace-nowrap"
              >
                <Shield className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{t('auditLogs.tabTitle')}</span>
                <span className="sm:hidden">{t('tabs.auditShort')}</span>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="applications" className="space-y-6 sm:space-y-8">
            {/* Department Filter Bar - GOK Exclusive */}
            <Card className="p-4 bg-gradient-to-r from-amber-50 to-amber-100/50 border border-amber-200 shadow-sm">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="flex items-center gap-2">
                  <Filter className="w-5 h-5 text-amber-700" />
                  <span className="text-sm font-semibold text-amber-800">{t('gok.departmentView')}:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant={departmentFilter === "all" ? "default" : "outline"}
                    onClick={() => setDepartmentFilter("all")}
                    className={departmentFilter === "all" 
                      ? "bg-amber-700 hover:bg-amber-800 text-white" 
                      : "border-amber-300 text-amber-800 hover:bg-amber-100"
                    }
                    data-testid="filter-all-departments"
                  >
                    <Globe className="w-4 h-4 mr-1" />
                    {t('gok.allDepartments')}
                  </Button>
                  <Button
                    size="sm"
                    variant={departmentFilter === "revenue" ? "default" : "outline"}
                    onClick={() => setDepartmentFilter("revenue")}
                    className={departmentFilter === "revenue" 
                      ? "bg-emerald-700 hover:bg-emerald-800 text-white" 
                      : "border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                    }
                    data-testid="filter-revenue"
                  >
                    <Building2 className="w-4 h-4 mr-1" />
                    {t('department.revenue')}
                  </Button>
                  <Button
                    size="sm"
                    variant={departmentFilter === "survey" ? "default" : "outline"}
                    onClick={() => setDepartmentFilter("survey")}
                    className={departmentFilter === "survey" 
                      ? "bg-blue-700 hover:bg-blue-800 text-white" 
                      : "border-blue-300 text-blue-800 hover:bg-blue-100"
                    }
                    data-testid="filter-survey"
                  >
                    <MapPin className="w-4 h-4 mr-1" />
                    {t('department.survey')}
                  </Button>
                </div>
              </div>
            </Card>

            {/* Stats Grid */}
            {stats && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4"
              >
                <Card className="p-3 sm:p-5 bg-white border border-amber-200 shadow-sm" data-testid="gok-stat-total">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 bg-amber-100 rounded-lg">
                      <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-amber-700" />
                    </div>
                    <div>
                      <p className="text-xl sm:text-3xl font-manrope font-bold text-slate-900">{stats.total_applications}</p>
                      <p className="text-[10px] sm:text-xs text-slate-500">{t('stats.totalApplications')}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-3 sm:p-5 bg-white border border-stone-200 shadow-sm" data-testid="gok-stat-pending">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
                      <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-blue-700" />
                    </div>
                    <div>
                      <p className="text-xl sm:text-3xl font-manrope font-bold text-slate-900">{stats.pending}</p>
                      <p className="text-[10px] sm:text-xs text-slate-500">{t('stats.pendingReview')}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-3 sm:p-5 bg-white border border-stone-200 shadow-sm" data-testid="gok-stat-in-progress">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 bg-amber-100 rounded-lg">
                      <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-amber-700" />
                    </div>
                    <div>
                      <p className="text-xl sm:text-3xl font-manrope font-bold text-slate-900">{stats.in_progress}</p>
                      <p className="text-[10px] sm:text-xs text-slate-500">{t('stats.inProgress')}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-3 sm:p-5 bg-white border border-stone-200 shadow-sm" data-testid="gok-stat-approved">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 bg-emerald-100 rounded-lg">
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-700" />
                    </div>
                    <div>
                      <p className="text-xl sm:text-3xl font-manrope font-bold text-slate-900">{stats.approved}</p>
                      <p className="text-[10px] sm:text-xs text-slate-500">{t('stats.approved')}</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-3 sm:p-5 bg-white border border-stone-200 shadow-sm" data-testid="gok-stat-rejected">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 bg-red-100 rounded-lg">
                      <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-700" />
                    </div>
                    <div>
                      <p className="text-xl sm:text-3xl font-manrope font-bold text-slate-900">{stats.rejected}</p>
                      <p className="text-[10px] sm:text-xs text-slate-500">{t('stats.rejected')}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Search and Filter */}
            <Card className="p-4 sm:p-6 bg-white border border-stone-200 shadow-sm">
              <div className="flex flex-col md:flex-row gap-3 sm:gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder={t('search.placeholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="gok-search-input"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-stone-200 rounded-md bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-600"
                  data-testid="gok-status-filter"
                >
                  <option value="all">{t('search.allStatus')}</option>
                  <option value="submitted">{t('status.submitted')}</option>
                  <option value="assigned">{t('status.assigned')}</option>
                  <option value="document_verification">{t('status.document_verification')}</option>
                  <option value="field_verification">{t('status.field_verification')}</option>
                  <option value="under_review">{t('status.under_review')}</option>
                  <option value="survey_inspection">{t('status.survey_inspection')}</option>
                  <option value="boundary_verification">{t('status.boundary_verification')}</option>
                  <option value="sketch_preparation">{t('status.sketch_preparation')}</option>
                  <option value="order_issued">{t('status.order_issued')}</option>
                  <option value="approved">{t('status.approved')}</option>
                  <option value="rejected">{t('status.rejected')}</option>
                </select>
              </div>
            </Card>

            {/* Applications Table */}
            <Card className="bg-white border border-stone-200 shadow-sm overflow-hidden">
              {/* Desktop: Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full" data-testid="gok-applications-table">
                  <thead className="bg-amber-50 border-b border-amber-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-amber-700 uppercase tracking-wider">
                        {t('table.surveyNumber')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-amber-700 uppercase tracking-wider">
                        {t('table.farmer')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-amber-700 uppercase tracking-wider">
                        {t('department.selectDepartment')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-amber-700 uppercase tracking-wider">
                        {t('table.location')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-amber-700 uppercase tracking-wider">
                        {t('table.status')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-amber-700 uppercase tracking-wider">
                        {t('table.date')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-amber-700 uppercase tracking-wider">
                        {t('table.action')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-200">
                    {filteredApps.map((app) => (
                      <tr
                        key={app.id}
                        className="hover:bg-amber-50/50 transition-colors"
                        data-testid={`gok-application-row-${app.id}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-mono text-sm text-slate-900">{app.land_survey_number}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="text-sm font-medium text-slate-900">{app.farmer_name}</p>
                            <p className="text-xs text-slate-500">{app.farmer_email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge className={
                            app.department === "survey" 
                              ? "bg-blue-100 text-blue-800 border-blue-200" 
                              : app.department === "both"
                              ? "bg-purple-100 text-purple-800 border-purple-200"
                              : "bg-emerald-100 text-emerald-800 border-emerald-200"
                          }>
                            {t(`department.${app.department || 'revenue'}`)}
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-slate-900">
                            <span>{app.village}</span>
                            {app.hobli && <span className="text-slate-500">, {app.hobli}</span>}
                          </div>
                          <div className="text-xs text-slate-500">
                            {app.taluk && <span>{app.taluk}, </span>}
                            {app.district}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge status={app.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-slate-500">{format(new Date(app.created_at), "MMM dd, yyyy")}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/application/${app.id}`)}
                            className="text-amber-700 hover:text-amber-900 hover:bg-amber-50"
                            data-testid={`gok-view-application-${app.id}`}
                          >
                            {t('table.viewDetails')}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredApps.length === 0 && (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">{t('table.noApplicationsFound')}</p>
                  </div>
                )}
              </div>

              {/* Mobile: Card View */}
              <div className="md:hidden">
                {filteredApps.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                    <p className="text-slate-500">{t('table.noApplicationsFound')}</p>
                  </div>
                ) : (
                  <div className="divide-y divide-stone-200">
                    {filteredApps.map((app) => (
                      <div
                        key={app.id}
                        className="p-4 hover:bg-amber-50/50 transition-colors"
                        data-testid={`gok-application-card-${app.id}`}
                      >
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-mono text-sm font-semibold text-amber-700">{app.land_survey_number}</p>
                            <p className="text-sm font-medium text-slate-900 mt-1">{app.farmer_name}</p>
                          </div>
                          <StatusBadge status={app.status} />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                          <div>
                            <p className="text-xs text-slate-500">{t('department.selectDepartment')}</p>
                            <Badge className={
                              app.department === "survey" 
                                ? "bg-blue-100 text-blue-800 text-xs" 
                                : app.department === "both"
                                ? "bg-purple-100 text-purple-800 text-xs"
                                : "bg-emerald-100 text-emerald-800 text-xs"
                            }>
                              {t(`department.${app.department || 'revenue'}`)}
                            </Badge>
                          </div>
                          <div className="col-span-2">
                            <p className="text-xs text-slate-500">{t('table.location')}</p>
                            <p className="text-slate-900">
                              {app.village}{app.hobli && `, ${app.hobli}`}
                              {app.taluk && `, ${app.taluk}`}, {app.district}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-500">
                            {format(new Date(app.created_at), "MMM dd, yyyy")}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/application/${app.id}`)}
                            className="text-amber-700 border-amber-200 hover:bg-amber-50"
                            data-testid={`gok-view-application-mobile-${app.id}`}
                          >
                            {t('table.viewDetails')}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="analytics">
            <AnalyticsDashboard />
          </TabsContent>

          <TabsContent value="officials">
            <OfficialsManagement user={user} />
          </TabsContent>

          <TabsContent value="gok-management">
            <GOKOfficialsManagement user={user} />
          </TabsContent>

          <TabsContent value="location-reports">
            <LocationBasedReports user={user} />
          </TabsContent>

          <TabsContent value="audit-logs">
            <AuditLogs />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
